import { useState } from "react";
import { toast } from "sonner";
import { BellRing, MapPin } from "lucide-react";
import { api, errMsg, fmtDT, DAYS, SERVICES } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { enablePush, pushPermission, pushSupported } from "../../lib/push";
import { Btn, Card, Row, Section } from "../../components/kit";
import ChangePassword from "../../components/ChangePassword";

export function PushSetup() {
  const [perm, setPerm] = useState(pushPermission());
  const [busy, setBusy] = useState(false);
  const on = async () => {
    setBusy(true);
    try {
      await enablePush();
      setPerm("granted");
      toast.success("Push alerts enabled on this device");
    } catch (e) {
      toast.error(e.message || errMsg(e));
      setPerm(pushPermission());
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section title="Push alerts" icon={BellRing} testid="push-setup">
      <div className="text-sm text-neutral-400 space-y-1">
        <p>Status: <span className="text-white" data-testid="push-status">{perm === "unsupported" ? "Not supported in this browser" : perm}</span></p>
        <p>Android: use Chrome, tap Enable, then Allow.</p>
        <p>iPhone (iOS 16.4+): in Safari tap Share → Add to Home Screen, open the app from the Home Screen, then tap Enable.</p>
        <p className="text-neutral-500">Push alerts never include the customer's address or contact. Delivery isn't guaranteed when your phone is asleep — offers always show in the app.</p>
      </div>
      <div className="flex gap-2">
        <Btn onClick={on} loading={busy} disabled={!pushSupported()} data-testid="push-enable-btn">Enable on this device</Btn>
        {perm === "granted" && <Btn variant="outline" onClick={() => api.post("/push/test").then(() => toast("Test sent")).catch((e) => toast.error(errMsg(e)))} data-testid="push-test-btn">Send test</Btn>}
      </div>
    </Section>
  );
}

export default function Profile() {
  const { user } = useAuth();
  const av = user.availability || {};
  const loc = user.location;
  const names = Object.fromEntries(SERVICES);
  return (
    <div className="space-y-4 fade-in" data-testid="specialist-profile">
      <h1 className="font-head text-3xl uppercase font-bold">{user.name}</h1>
      <Card>
        <Row k="Email" v={user.email} />
        <Row k="Categories" v={(user.categories || []).join(", ") || "—"} />
        <Row k="Service radius" v={`${user.radius_miles} mi`} />
        <Row k="Schedule" v={av.days?.length ? `${av.days.map((d) => DAYS[d]).join(" ")} · ${av.start}–${av.end}` : "Not set (any time)"} />
        <Row k="Approved services" v={(user.approved_services || []).map((k) => names[k]).join(", ") || "—"} />
      </Card>
      <Card className="text-sm space-y-1" data-testid="location-info">
        <div className="flex gap-2 items-center"><MapPin className="w-4 h-4 text-[#FF6B00]" />{loc ? loc.label : "No location yet"}</div>
        {loc && <div className="text-xs text-neutral-500">Updated {fmtDT(loc.updated_at)} · {loc.approx ? "approximate" : "device GPS"} · captured only when you go Available</div>}
      </Card>
      <PushSetup />
      <ChangePassword />
      <p className="text-xs text-neutral-500">Admin manages your approved services, radius and schedule. Contact Driven Auto Care to change them.</p>
    </div>
  );
}
